import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, ExternalLink } from "lucide-react";

const emptyForm = { name: "", description: "" };

export default function MyCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies/mine");
      setCompanies(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || "" }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    try {
      if (editing) {
        await api.put(`/companies/${editing.company_id}`, form);
        toast.success("Empresa actualizada");
      } else {
        await api.post("/companies", form);
        toast.success("Empresa creada");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Empresa eliminada");
      load();
    } catch { toast.error("Error"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Mis empresas</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" onClick={openNew} className="rounded-full" data-testid="new-company-btn">
              <Plus className="w-4 h-4 mr-2" /> Nueva empresa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Editar empresa" : "Nueva empresa"}</DialogTitle>
              <DialogDescription>Podrás elegir esta empresa al publicar un proyecto.</DialogDescription>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs tracking-widest uppercase font-semibold">Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Acme Corp" required className="mt-1.5 h-11" data-testid="company-name-input" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-semibold">Descripción</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Qué hace la empresa…" className="mt-1.5" data-testid="company-description-input" />
              </div>
              <Button type="submit" className="rounded-full w-full h-11" data-testid="save-company-btn">
                {editing ? "Guardar cambios" : "Crear empresa"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Cargando…</div>
      ) : companies.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center">
          <Building2 className="w-6 h-6 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">Aún no has añadido ninguna empresa.</p>
          <Button onClick={openNew} className="rounded-full" data-testid="empty-new-company-btn">
            <Plus className="w-4 h-4 mr-2" /> Añadir mi primera empresa
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <Card key={c.company_id} data-testid={`company-card-${c.company_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-lg font-semibold tracking-tight truncate">{c.name}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Editar" data-testid={`edit-company-${c.company_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" title="Eliminar" data-testid={`delete-company-${c.company_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar {c.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Los proyectos ya publicados conservarán el nombre de la empresa como texto, pero perderán la asociación con este registro.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(c.company_id)} data-testid={`confirm-delete-company-${c.company_id}`}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                {c.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{c.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
